import PropTypes from 'prop-types';
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { Icon, PlainButton, inkA, orange, tokens } from 'components/v3';
import { formatDate } from '../shared/dates';
import { FormStatusPill, StatusSwitch } from './FormStatus';

// One form on the Forms list (design: listview.jsx FormCard). The whole
// card is a PlainButton that opens the builder (stretched behind the
// content); the Live switch and the Notes / Submissions / Restore buttons
// sit on top of it as siblings, never nested inside it. The Archived view
// shows Restore instead of the switch.

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-width: 0;
  padding: ${(p) => (p.$compact ? '16px' : '24px')};
  border-radius: 16px;
  background: ${(p) => p.$bg};
  border: 1px solid ${(p) => p.$border};
  box-shadow: ${(p) => p.$shadow};
  transition: border-color 150ms ease, transform 150ms ease,
    box-shadow 150ms ease;

  &:hover {
    border-color: ${orange(0.7)};
    transform: translateY(-2px);
  }

  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

const OpenButton = styled(PlainButton)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border-radius: 16px;
`;

// Layers above the stretched button. Text lets clicks fall through to it;
// controls take them.
const Passive = styled.div`
  position: relative;
  pointer-events: none;
`;

const Active = styled.div`
  position: relative;
  z-index: 1;
  pointer-events: auto;
`;

const CardButton = styled(PlainButton)`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 7px 12px;
  box-sizing: border-box;
  border-radius: 8px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;

  &:hover:not(:disabled) {
    border-color: ${orange(1)};
    color: ${orange(1)};
    background: ${orange(0.08)};
  }

  &:disabled {
    opacity: 0.6;
  }
`;

// "Created Jun 16, 2026" with its icon (design .fc-date).
const DateRow = ({ icon, label, value }) => {
  const t = tokens(useTheme());
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        color: t.faint,
      }}
    >
      <Icon name={icon} size={13} color="currentColor" />
      <span>
        {`${label} `}
        <strong style={{ fontWeight: 600, color: t.sub }}>{value}</strong>
      </span>
    </span>
  );
};

DateRow.propTypes = {
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

// A count over its label (design .fc-stat).
const Stat = ({ value, label }) => {
  const t = tokens(useTheme());
  return (
    <div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1.2,
          color: t.text,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {Number(value || 0).toLocaleString()}
      </div>
      <div
        style={{
          marginTop: 2,
          fontSize: 11,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          color: t.faint,
        }}
      >
        {label}
      </div>
    </div>
  );
};

Stat.propTypes = {
  value: PropTypes.number,
  label: PropTypes.string.isRequired,
};

Stat.defaultProps = { value: 0 };

const FormCard = ({
  form,
  archived,
  busy,
  compact,
  onOpen,
  onOpenSubmissions,
  onToggle,
  onOpenNotes,
  onRestore,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const neutral = { $border: t.border, $bg: 'transparent', $color: t.sub };
  const noteCount = form.note_count || 0;

  return (
    <Card
      data-testid={`form-card-${form.id}`}
      data-tour="form-card"
      $compact={compact}
      $bg={t.card}
      $border={t.border}
      $shadow={t.shadowCard}
    >
      <OpenButton
        aria-label={`Open ${form.name}`}
        onClick={() => onOpen(form)}
      />
      <Passive
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 10,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 8,
              maxWidth: '100%',
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
              color: t.primary,
            }}
          >
            <Icon name="flag" size={12} color="currentColor" />
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {form.account ? form.account.name : 'Unassigned'}
            </span>
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              lineHeight: 1.25,
              color: t.text,
              overflowWrap: 'anywhere',
            }}
          >
            {form.name}
          </h2>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 3,
              marginTop: 7,
            }}
          >
            <DateRow
              icon="calendar-plus"
              label="Created"
              value={formatDate(form.created_at)}
            />
            <DateRow
              icon="pen"
              label="Updated"
              value={formatDate(form.updated_at)}
            />
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <FormStatusPill status={form.status} />
        </div>
      </Passive>

      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 24,
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid ' + t.line,
          pointerEvents: 'none',
        }}
      >
        <Stat value={form.submission_count} label="Submissions" />
        <Stat value={form.field_count} label="Fields" />
        {archived ? null : (
          <Active
            data-tour="form-card-status"
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                color: t.faint,
              }}
            >
              {form.status === 'live' ? 'On' : 'Off'}
            </span>
            <StatusSwitch
              status={form.status}
              label={`Live: ${form.name}`}
              disabled={busy}
              onChange={(next) => onToggle(form, next)}
            />
          </Active>
        )}
      </div>

      <Active
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 8,
          marginTop: 14,
        }}
      >
        <CardButton
          data-tour="form-card-notes"
          onClick={() => onOpenNotes(form)}
          aria-label={
            noteCount
              ? `Notes (${noteCount}) for ${form.name}`
              : `Add a note to ${form.name}`
          }
          {...neutral}
          {...(noteCount
            ? { $border: orange(0.3), $bg: orange(0.06), $color: t.primary }
            : {})}
        >
          <Icon name="message-square" size={15} color="currentColor" />
          {noteCount ? `Notes · ${noteCount}` : 'Add a note'}
        </CardButton>
        {archived ? (
          <CardButton
            onClick={() => onRestore && onRestore(form)}
            disabled={busy}
            aria-label={`Restore ${form.name}`}
            style={{ marginLeft: 'auto' }}
            {...neutral}
            $color={t.text}
            $bg={inkA(theme, 0.04)}
          >
            <Icon name="archive-restore" size={15} color="currentColor" />
            Restore
          </CardButton>
        ) : (
          <CardButton
            data-tour="form-card-submissions"
            onClick={() => onOpenSubmissions(form)}
            aria-label={`Submissions for ${form.name}`}
            style={{ marginLeft: 'auto' }}
            {...neutral}
          >
            <Icon name="inbox" size={15} color="currentColor" />
            Submissions
            <span style={{ display: 'inline-flex', opacity: 0.6 }}>
              <Icon name="chevron-right" size={14} color="currentColor" />
            </span>
          </CardButton>
        )}
      </Active>
    </Card>
  );
};

FormCard.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['live', 'draft']).isRequired,
    account: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }),
    field_count: PropTypes.number,
    submission_count: PropTypes.number,
    note_count: PropTypes.number,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
  }).isRequired,
  archived: PropTypes.bool,
  busy: PropTypes.bool,
  compact: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onOpenSubmissions: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onOpenNotes: PropTypes.func.isRequired,
  onRestore: PropTypes.func,
};

FormCard.defaultProps = {
  archived: false,
  busy: false,
  compact: false,
  onRestore: undefined,
};

export default FormCard;
